import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { pageDataKeys } from "~/app/api/pageData";
import { useIsSmartEdit } from "~/app/config/pageMetadataContext";
import { queryClient } from "~/app/queryClient";

type RefreshHandlerDependencies = {
  refreshPage: () => Promise<void>;
  afterRender: () => Promise<void>;
  reprocessPage: () => void;
  reloadPage: () => void;
};

const SHARED_PAGE_DATA_KEYS = new Set(["bootstrap", "session"]);

type RouteRefreshMatch = {
  status: string;
  error?: unknown;
};

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

export function assertRouteRefreshSucceeded(matches: readonly RouteRefreshMatch[]): void {
  const failedMatch = matches.find(({ status }) => status === "error");
  if (!failedMatch) {
    return;
  }
  if (failedMatch.error instanceof Error) {
    throw failedMatch.error;
  }
  throw new Error("SmartEdit React route refresh failed");
}

export function createSmartEditContentRefreshHandler({
  refreshPage,
  afterRender,
  reprocessPage,
  reloadPage,
}: RefreshHandlerDependencies): () => boolean {
  let refreshInFlight: Promise<void> | null = null;

  return () => {
    if (!refreshInFlight) {
      refreshInFlight = refreshPage()
        .then(afterRender)
        .then(reprocessPage)
        .catch(reloadPage)
        .finally(() => {
          refreshInFlight = null;
        });
    }

    // SAP SmartEdit treats a truthy result as confirmation that the storefront
    // owns the refresh and must not fall back to replacing React-managed HTML.
    return true;
  };
}

/**
 * Bridges SAP SmartEdit's storefront refresh hook to the React page-data owner.
 * SmartEdit emits slot/component refresh requests after CMS mutations. The
 * legacy fallback parses a fresh HTML document and swaps slot markup directly,
 * which cannot update React state. Invalidate route page data instead, rerun the
 * active loader, and let SmartEdit rebuild its overlay after React commits.
 */
export function SmartEditContentRefresh() {
  const inSmartEdit = useIsSmartEdit();
  const router = useRouter();

  useEffect(() => {
    if (!inSmartEdit) {
      return;
    }

    const renderComponent = createSmartEditContentRefreshHandler({
      refreshPage: async () => {
        await queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] === pageDataKeys.all[0] && !SHARED_PAGE_DATA_KEYS.has(String(queryKey[1])),
          refetchType: "none",
        });
        await router.invalidate({ sync: true });
        // TanStack Router records ordinary loader failures on the route match
        // and still resolves invalidate(), so inspect the committed result.
        assertRouteRefreshSucceeded(router.state.matches);
      },
      afterRender: waitForNextPaint,
      reprocessPage: () => window.smartedit?.reprocessPage?.(),
      reloadPage: () => window.location.reload(),
    });
    const registrations: Array<{
      namespace: NonNullable<Window["smartedit"]>;
      previousRenderComponent: NonNullable<Window["smartedit"]>["renderComponent"];
    }> = [];
    let mounted = true;

    const installRenderComponent = () => {
      if (!mounted) {
        return;
      }
      const namespace = (window.smartedit ??= {});
      if (namespace.renderComponent === renderComponent) {
        return;
      }
      registrations.push({ namespace, previousRenderComponent: namespace.renderComponent });
      namespace.renderComponent = renderComponent;
    };

    installRenderComponent();
    // SmartEdit injects its inner Angular application after the storefront has
    // mounted. Reinstall once that application starts decorating the storefront,
    // because its bootstrap can publish a fresh namespace after this effect.
    const bootstrapObserver = new MutationObserver(() => {
      if (!document.querySelector("smartedit-element")) {
        return;
      }
      installRenderComponent();
      bootstrapObserver.disconnect();
    });
    bootstrapObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mounted = false;
      bootstrapObserver.disconnect();
      for (const { namespace, previousRenderComponent } of [...registrations].reverse()) {
        if (namespace.renderComponent !== renderComponent) {
          continue;
        }
        if (previousRenderComponent) {
          namespace.renderComponent = previousRenderComponent;
        } else {
          delete namespace.renderComponent;
        }
      }
    };
  }, [inSmartEdit, router]);

  return null;
}
